import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { SopMarkdown } from "@/components/manual/SopMarkdown";
import { DocBodyBoundary } from "@/components/manual/DocBodyBoundary";
import { memberDomainLabel } from "@/lib/domainLabel";
import { docTypeLabel, stripStartHerePrefix } from "@/lib/docTypeLabel";
import { stripDocIdPrefix } from "@/lib/docTitle";
import { docHref } from "@/lib/sopLinks";
import { extractTrailingDisclaimer, stripEmbeddedMetaLine } from "@/lib/docBodyClean";
import { RelatedActions, parseRelatedActions } from "@/components/library/RelatedActions";

import { DocFeedbackWidget } from "@/components/manual/DocFeedbackWidget";
import { DocMetaFooter } from "@/components/manual/DocMetaFooter";


const DOC_ID_PATTERN = /^[A-Z]{2,5}-[A-Z0-9-]+$/;

/**
 * Unified library document page. Serves both collections (sop and faq)
 * plus the special dictionary route. The slug lookup already knows the
 * collection, so a single component works for every doc URL under
 * /support/library/*.
 */
export default function LibraryDocument() {
  const params = useParams<{ slug?: string }>();
  // /support/library/dictionary is a fixed route with no :slug param.
  const isDictionaryRoute = params.slug === undefined || params.slug === "dictionary";
  const routeSeg = isDictionaryRoute ? undefined : params.slug;

  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [serviceCallout, setServiceCallout] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    setRedirectTo(null);
    (async () => {
      if (isDictionaryRoute) {
        const { data } = await supabase
          .from("sop_documents" as any)
          .select("*")
          .eq("doc_id", "FAQ-000")
          .eq("status", "published")
          .maybeSingle();
        setDoc(data);
        setLoading(false);
        return;
      }
      if (!routeSeg) return;

      // Try slug first, across both collections.
      let { data } = await supabase
        .from("sop_documents" as any)
        .select("*")
        .eq("slug", routeSeg)
        .eq("status", "published")
        .maybeSingle();

      // Fallback to doc_id lookup, then permanent redirect to the slug URL.
      if (!data && DOC_ID_PATTERN.test(routeSeg)) {
        const res = await supabase
          .from("sop_documents" as any)
          .select("*")
          .eq("doc_id", routeSeg)
          .eq("status", "published")
          .maybeSingle();
        data = res.data;
        if (data) {
          setRedirectTo(
            docHref({
              doc_id: (data as any).doc_id,
              collection: (data as any).collection,
              slug: (data as any).slug,
            }) +
              window.location.search +
              window.location.hash,
          );
          return;
        }
      }
      setDoc(data);
      setLoading(false);
    })();
  }, [routeSeg, isDictionaryRoute]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings" as any)
        .select("key,value")
        .in("key", ["services_launched", "services_callout_md"]);
      const map = new Map((data || []).map((r: any) => [r.key, r.value]));
      if (map.get("services_launched") === "true" && (map.get("services_callout_md") || "").trim()) {
        setServiceCallout(map.get("services_callout_md") as string);
      } else {
        setServiceCallout("");
      }
    })();
  }, []);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  const collection = (doc?.collection as string) || "sop";
  const isFaq = collection === "faq";

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/support/library"><ArrowLeft className="h-4 w-4 mr-1" /> Back to library</Link>
        </Button>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : !doc ? (
          <Card className="p-8 text-center text-muted-foreground">
            {isFaq || isDictionaryRoute ? "Article" : "Document"} not found or not published.
          </Card>
        ) : (
          <article>
            {(() => {
              const raw = doc.body_markdown || "";
              const cleaned = stripEmbeddedMetaLine(raw);
              const { body, disclaimer } = extractTrailingDisclaimer(cleaned);
              return (
                <Card className="p-5 md:p-12">
                  <h1 className="grt-article-title font-serif mb-2">
                    {stripStartHerePrefix(stripDocIdPrefix(doc.title))}
                  </h1>


                  <p className="text-sm mb-8">
                    <Link
                      to={`/support/library?section=${encodeURIComponent(memberDomainLabel(doc.domain, collection as any))}`}
                      className="font-medium text-[hsl(35_45%_28%)] hover:underline hover:text-[hsl(35_55%_22%)]"
                      aria-label={`Open ${memberDomainLabel(doc.domain, collection as any)} in the library`}
                    >
                      {memberDomainLabel(doc.domain, collection as any)}
                    </Link>
                    <span className="mx-2 text-muted-foreground" aria-hidden="true">·</span>
                    <span className="text-muted-foreground">{docTypeLabel(collection)}</span>
                  </p>

                  <DocBodyBoundary>
                    <SopMarkdown body={body} docId={doc.doc_id} />
                  </DocBodyBoundary>

                  <RelatedActions actions={parseRelatedActions(doc.related_actions)} />



                  {serviceCallout && (
                    <div className="mt-8 border-l-4 border-l-gold pl-5 py-2">
                      <DocBodyBoundary>
                        <SopMarkdown body={serviceCallout} />
                      </DocBodyBoundary>
                    </div>
                  )}

                  <hr className="mt-8 mb-3 border-border/60" />

                  <div className="flex items-baseline justify-between">
                    <DocFeedbackWidget
                      docId={doc.doc_id}
                      collection={collection as any}
                      docVersion={doc.version}
                      variant={isDictionaryRoute ? "dictionary" : "standard"}
                      inline
                    />
                    <Link to="/support" className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted whitespace-nowrap">
                      Ask Stew about this
                    </Link>
                  </div>

                  {disclaimer && (
                    <p className="mt-6 text-xs italic text-muted-foreground">{disclaimer}</p>
                  )}
                </Card>
              );
            })()}
            <DocMetaFooter
              docId={doc.doc_id}
              title={doc.title}
              version={doc.version}
              updatedAt={doc.updated_at}
              collection={collection as any}
              slug={doc.slug}
            />
          </article>
        )}
      </div>
    </MainLayout>
  );
}
